import React, { useEffect, useState } from "react";
import "./index.css";
import {
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  TextField,
} from "@mui/material";
import { LuFilePen, LuEye, LuTrash2, LuHardDriveDownload } from "react-icons/lu";
import { TbPlayerTrackPrev, TbPlayerTrackNext } from "react-icons/tb";
import { TbFilterCog } from 'react-icons/tb';
import MaterialInput from "../MaterialInput";
import { TbFilterCancel } from 'react-icons/tb';

const SimpleTable = ({
  columns,
  data,
  onEdit,
  onView,
  onDelete,
  onDownload,
  download = false,
  view = true,
  edit = true,
  deleteAction = true,
  isLoading = false,
  currentPageProp = 1,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState("");
  const rowsPerPage = 10;

  useEffect(() => {
    if (currentPageProp) setCurrentPage(currentPageProp);
  }, [currentPageProp]);

  // Sorting
  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    if (typeof aValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
  });


  const filteredData =
    filterText && selectedColumn
      ? sortedData.filter((row) =>
        row[selectedColumn]
          ?.toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())
      )
      : sortedData;


  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const truncate = (desc) =>
    desc?.length > 15 ? `${desc.substring(0, 15)}...` : desc;

  const handleSort = (key) => {
    if (key === "action") return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };


  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleColumnSelect = (key) => {
    setSelectedColumn((prev) => (prev === key ? "" : key));
    setFilterText("");
    handleMenuClose();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleFilterClear =() => {
    setFilterText("");
    setSelectedColumn("");
  }

  const renderSkeletonRows = () =>
    Array.from({ length: rowsPerPage }).map((_, i) => (
      <tr key={`sk-${i}`}>
        {columns.map((_, j) => (
          <td key={j}>
            <div className="skeleton-line" />
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="table-container">
      <div className="filter-container" style={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ display: 'flex', padding: 0.5, height: '28px', width: '30px', borderRadius: '5px', backgroundColor: '#5e6cbf', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', }} onClick={handleMenuOpen}>
          <TbFilterCog size={30} />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {columns
            .filter((col) => col.key !== "action")
            .map((col) => (
              <MenuItem
                key={col.key}
                onClick={() => handleColumnSelect(col.key)}
                sx={{
                  py: 0.3,
                  minHeight: '30px',
                }}
              >
                <Checkbox
                  checked={selectedColumn === col.key}
                  size="small"
                  sx={{
                    padding: 0.3,
                    transform: 'scale(0.8)',
                  }}
                />
                <ListItemText
                  primary={col.header}
                  primaryTypographyProps={{
                    fontSize: '13px',
                  }}
                />
              </MenuItem>
            ))}
        </Menu>



        {selectedColumn && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <MaterialInput
              size="small"
              variant="outlined"
              placeholder={`Search in ${columns.find(c => c.key === selectedColumn)?.header || ""}`}
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setCurrentPage(1);
              }}
              sx={{ marginLeft: "10px", width: "300px" }}
            />
            <Box sx={{ display: 'flex', padding: 0.5, height: '28px', width: '30px', borderRadius: '5px', backgroundColor: '#EA7473', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', }} onClick={handleFilterClear}>
              <TbFilterCancel size={30} color="#fff" />
            </Box>
          </Box>
        )}
      </div>


      <div className="table-scroll">
        <table className="reusable-table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(column.key)}
                  className={column.key !== "action" ? "sortable" : ""}
                >
                  <div className="header-content">
                    {column.header}
                    {!isLoading && sortConfig.key === column.key && (
                      <span className="sort-icon">
                        {sortConfig.direction === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isLoading
              ? renderSkeletonRows()
              : currentData.length > 0
                ? currentData.map((row, i) => (
                  <tr key={i}>
                    {columns.map((column, j) => (
                      <td key={j}>
                        {column.key === "action" ? (
                          <div className="action-buttons">
                            {edit && (
                              <LuFilePen
                                className="action-icon edit-icon"
                                onClick={() => onEdit(row.id, row, currentPage)}
                                title="Edit"
                              />
                            )}
                            {view && (
                              <LuEye
                                className="action-icon view-icon"
                                onClick={() => onView(row.id, row)}
                                title="View"
                              />
                            )}
                            {deleteAction && (
                              <LuTrash2
                                className="action-icon delete-icon"
                                onClick={() => onDelete(row.id, row, currentPage)}
                                title="Delete"
                              />
                            )}
                            {download && (
                              <LuHardDriveDownload
                                className="action-icon delete-icon"
                                onClick={() => onDownload(row.id, row)}
                                title="Download"
                              />
                            )}
                          </div>
                        ) : (
                          <Tooltip title={row[column.key]} arrow>
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                                maxWidth: "100%",
                              }}
                            >
                              {truncate(row[column.key])}
                            </span>
                          </Tooltip>
                        )}
                      </td>
                    ))}
                  </tr>
                ))
                : (
                  <tr>
                    <td colSpan={columns.length} className="no-data">
                      No data found
                    </td>
                  </tr>
                )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && totalRows > 0 && (
        <div className="pagination-container">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.5 : 1,
              padding: "5px",
              borderRadius: "50%",
              backgroundColor: "#5e6cbf",
              height: "30px",
              width: "30px",
            }}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <TbPlayerTrackPrev size={18} color="#fff" />
          </Box>

          {[...Array(totalPages)].map((_, page) => (
            <Box
              key={page + 1}
              className={`pagination-button ${page + 1 === currentPage ? "active" : ""
                }`}
              onClick={() => handlePageChange(page + 1)}
            >
              {page + 1}
            </Box>
          ))}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              opacity: currentPage === totalPages ? 0.5 : 1,
              padding: "5px",
              borderRadius: "50%",
              backgroundColor: "#5e6cbf",
              height: "30px",
              width: "30px",
            }}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <TbPlayerTrackNext size={18} color="#fff" />
          </Box>
        </div>
      )}
    </div>
  );
};

export default SimpleTable;
